import React, { useState, useCallback, useEffect, useRef } from 'react';

// --- Constantes ---
const HOSPITAL_PHONE = '+52 55 1234 5678';
const WHATSAPP_NUMBER = '525512345678'; 
const LOCAL_STORAGE_KEY = 'virtualMedicalAssistantChat';

// =========================================================================
// INLINE SVG ICONS (Modernizados)
// =========================================================================

const IconWrapper = ({ children, className, ...props }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className={className} 
        {...props}
    >
        {children}
    </svg>
);

const PhoneIcon = (props) => (
    <IconWrapper {...props}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-3.71-3.71 19.5 19.5 0 0 1-3.07-8.63A2 2 0 0 1 4.08 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></IconWrapper>
);

const CalendarIcon = (props) => (
    <IconWrapper {...props}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></IconWrapper>
);

const MessageCircleIcon = (props) => (
    <IconWrapper {...props}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 14.16 14.16 0 0 1-2.4 3.7c-1.3 1.5-2.9 2.7-4.8 3.5a1.8 1.8 0 0 1-2 0c-1.9-.8-3.5-2-4.8-3.5a14.16 14.16 0 0 1-2.4-3.7 8.38 8.38 0 0 1-.9-3.8c-.3-1.6-.3-3.2 0-4.8A8.38 8.38 0 0 1 4.5 3.7c.9-1.2 2.1-2.2 3.6-2.8 1.5-.6 3.2-.8 5-.4 1.8.4 3.4 1.3 4.7 2.6A8.38 8.38 0 0 1 21 11.5z"/></IconWrapper>
);

const SendIcon = (props) => (
    <IconWrapper {...props}><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></IconWrapper>
);

const ClockIcon = (props) => (
    <IconWrapper {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></IconWrapper>
);

// Icono para Limpiar/Papelera
const Trash2Icon = (props) => (
    <IconWrapper {...props}><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></IconWrapper>
);


// =========================================================================
// CENTRALIZED RESPONSE CONTENT RESOLVER
// =========================================================================

/**
 * Retorna el JSX para el menú inicial.
 */
const getInitialMenuJSX = () => (
  <div className="space-y-2">
    <p className="font-semibold text-blue-800">¡Hola! Soy tu Asistente Médico Virtual. ¿En qué puedo ayudarte hoy?</p>
    <p>Por favor, selecciona una opción escribiendo el número correspondiente:</p>
    <ul className="list-disc list-inside ml-2 space-y-1 font-medium">
      <li><strong>1.</strong> 🗓️ Agendar una cita</li>
      <li><strong>2.</strong> 💬 Hablar con un Dr. (Abrirá WhatsApp)</li>
      <li><strong>3.</strong> 📞 Llamar al Hospital</li>
    </ul>
  </div>
);

/**
 * Función central para mapear las claves de respuesta (strings) al contenido JSX.
 * @param {string} messageContent - La clave de string o el texto de usuario.
 * @returns {React.ReactNode} El contenido JSX o el string de texto plano.
 */
const resolveMessageContent = (messageContent) => {
    if (typeof messageContent !== 'string') {
        return "⚠️ Error al cargar el contenido del mensaje. Por favor, reinicia el chat.";
    }
    
    switch (messageContent) {
        case 'initial_menu':
            return getInitialMenuJSX();

        case 'response_1': // Agendar una cita
            return (
                <div className="space-y-2">
                    <h4 className="font-bold text-lg text-blue-600 flex items-center"><CalendarIcon className="w-5 h-5 mr-2"/> Cita Agendada (Simulación)</h4>
                    <p>¡Perfecto! Hemos registrado tu solicitud de cita. Por favor, revisa tu WhatsApp/correo.</p>
                    <p className="text-sm italic">Un agente te contactará pronto para confirmar fecha y hora.</p>
                    <div className="pt-2">{getInitialMenuJSX()}</div>
                </div>
            );

        case 'response_2': // Abrir WhatsApp
            return (
                <div className="space-y-2">
                    <h4 className="font-bold text-lg text-green-600 flex items-center"><MessageCircleIcon className="w-5 h-5 mr-2"/> Abriendo WhatsApp...</h4>
                    <p>Se ha abierto una nueva ventana para chatear con nuestro equipo médico.</p>
                    <p className="text-sm italic">Si no se abre, revisa la configuración de tu navegador.</p>
                    <div className="pt-2">{getInitialMenuJSX()}</div>
                </div>
            );

        case 'response_3': // Llamar al Hospital
            return (
                <div className="space-y-2">
                    <h4 className="font-bold text-lg text-red-600 flex items-center"><PhoneIcon className="w-5 h-5 mr-2"/> Llámanos Ahora</h4>
                    <p>Puedes llamarnos directamente para atención de emergencia o información:</p>
                    <a 
                      href={`tel:${HOSPITAL_PHONE.replace(/ /g, '')}`} 
                      className="font-mono text-xl text-red-600 hover:text-red-700 underline"
                    >
                      {HOSPITAL_PHONE}
                    </a>
                    <p className="text-sm italic">Haz clic en el número para iniciar la llamada.</p>
                    <div className="pt-2">{getInitialMenuJSX()}</div>
                </div>
            );

        case 'unrecognized_option': // Opción no reconocida
            return (
                <div className="space-y-2">
                    <p className="text-red-500 font-bold">⚠️ Opción no válida. Por favor, escribe sólo el número (1, 2 o 3) para seleccionar una opción del menú.</p>
                    {getInitialMenuJSX()}
                </div>
            );
            
        default:
            return messageContent; // Retorna el string de texto plano (mensaje de usuario)
    }
}


/**
 * Función de ayuda para cargar el estado desde localStorage con comprobación de corrupción.
 */
const loadInitialMessages = () => {
    try {
        const storedMessages = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (storedMessages) {
            const messages = JSON.parse(storedMessages);
            
            if (messages && messages.length > 0) {
                // Comprobación: Asegura que solo se carguen objetos con 'text' de tipo string.
                const isCorrupted = messages.some(msg => typeof msg.text !== 'string');
                
                if (!isCorrupted) {
                    return messages;
                }
            }
        }
    } catch (error) {
        console.error("Error al cargar el historial de chat de localStorage o datos corruptos. Iniciando nuevo chat.", error);
    }
    // Fallback: Usa la clave de string para el menú inicial.
    return [{ from: 'admin', text: 'initial_menu' }]; 
};


// =================================================================
// 1. Componente ChatBubble
// =================================================================
const ChatBubble = ({ message }) => {
  const isUser = message.from === 'user';
  
  // Resolvemos el contenido de la burbuja aquí antes de renderizar
  const content = resolveMessageContent(message.text);

  const bubbleClasses = isUser
    ? 'bg-blue-600 text-white self-end rounded-br-lg rounded-tl-xl rounded-tr-xl rounded-bl-xl'
    : 'bg-gray-200 text-gray-800 self-start rounded-tl-lg rounded-tr-xl rounded-br-xl rounded-bl-xl';
  const containerClasses = isUser ? 'justify-end' : 'justify-start';

  return (
    <div className={`flex ${containerClasses}`}>
      <div className={`max-w-[85%] sm:max-w-md p-4 rounded-xl shadow-md transition-all duration-300 ${bubbleClasses}`}>
        {content} {/* Renderiza el string o el JSX resuelto */}
      </div>
    </div>
  );
};

// =================================================================
// 2. Componente Principal App
// =================================================================
export default function App() {
  const [messages, setMessages] = useState(loadInitialMessages);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);

  // Scroll automático y guardado de persistencia
  useEffect(() => {
    scrollToBottom();
    // Guarda el estado en localStorage, solo contiene strings/claves
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(messages));
    } catch (error) {
        console.error("Error al guardar el historial de chat en localStorage:", error);
    }
  }, [messages]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const addMessage = useCallback((from, text) => {
    // Almacenamos solo strings (texto plano de usuario o clave de respuesta de admin)
    setMessages(prev => [...prev, { from, text }]);
  }, []);
  
  const handleAction = useCallback((option) => {
    let responseKey;
    
    const normalizedOption = option.trim();
    
    switch (normalizedOption) {
      case '1':
        responseKey = 'response_1';
        break;

      case '2':
        // Acciones específicas (abrir ventana)
        const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hola, me gustaría hablar con un doctor.')}`;
        window.open(whatsappUrl, '_blank');
        responseKey = 'response_2';
        break;

      case '3':
        responseKey = 'response_3';
        break;

      default:
        responseKey = 'unrecognized_option';
        break;
    }
    
    addMessage('admin', responseKey);
    setIsTyping(false); // Detener el indicador de escritura después de responder
  }, [addMessage]);
  
  /**
   * Limpia el estado del chat y el historial en localStorage, restableciendo al menú inicial.
   */
  const handleClearChat = () => {
      setMessages([{ from: 'admin', text: 'initial_menu' }]);
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      setInput(''); // Limpiar la entrada del usuario
      setIsTyping(false); // Asegurar que el indicador de escritura esté apagado
      console.log("Historial de chat limpiado.");
  };


  const handleSend = (e) => {
    e.preventDefault();
    const userText = input.trim();
    if (!userText) return;

    // Guardar el mensaje del usuario como string
    addMessage('user', userText);
    setInput('');
    setIsTyping(true); // Mostrar indicador de escritura

    // Simular tiempo de respuesta del asistente
    setTimeout(() => {
      handleAction(userText);
    }, 1000);
  };
  
  // Componente de diseño minimalista para envolver el chat (Layout)
  const Layout = ({ children }) => (
    <div className="flex flex-col h-screen bg-gray-100 font-sans">
      <header className="bg-blue-700 text-white p-4 flex items-center justify-between shadow-xl">
        <div className="flex items-center">
            <ClockIcon className="w-6 h-6 mr-3"/>
            <h1 className="text-2xl font-extrabold">Chat Medical Cleth </h1>
        </div>
        {/* BOTÓN PARA LIMPIAR EL CHAT (ICONO DE PAPELERA) */}
        <button 
            onClick={handleClearChat} // Función de limpieza
            title="Limpiar Conversación" // Título actualizado
            className="p-2 rounded-full hover:bg-blue-600 transition duration-150 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white"
        >
            <Trash2Icon className="w-6 h-6"/> {/* Icono de papelera */}
        </button>
      </header>
      <main className="flex-1 overflow-y-hidden">
        {children}
      </main>
    </div>
  );
  
  // Indicador de Escritura
  const TypingIndicator = () => (
    <div className="flex justify-start">
        <div className="bg-gray-200 text-gray-800 self-start p-3 rounded-xl rounded-tl-lg shadow-md flex items-center space-x-2">
            <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse delay-0"></div>
            <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse delay-150"></div>
            <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse delay-300"></div>
        </div>
    </div>
  );


  return (
    <Layout>
      <div className="flex flex-col h-full">
        {/* Cuerpo del Chat */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 border-b">
          {messages.map((msg, index) => (
            <ChatBubble key={index} message={msg} /> 
          ))}
          {isTyping && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Formulario de Entrada */}
        <form onSubmit={handleSend} className="p-4 bg-white border-t flex gap-3 shadow-2xl">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Escribe 1, 2 o 3 para seleccionar una opción..."
            className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-inner transition"
            autoFocus
            maxLength={1} // Limitar a un solo dígito para el menú
          />
          <button
            type="submit"
            className="bg-blue-600 text-white px-5 py-3 rounded-xl hover:bg-blue-700 transition duration-150 flex items-center justify-center font-semibold shadow-lg active:scale-95"
            disabled={!input.trim()}
          >
            <SendIcon className="w-5 h-5" />
          </button>
        </form>
      </div>
    </Layout>
  );
}